import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';

const rideCategoryOptions = [
  'Local ride help',
  'Pickup/drop-off',
  'Event ride help',
  'Senior/community ride help',
  'Non-passenger pickup/delivery preference',
];

const acknowledgementOptions = [
  {
    key: 'hasValidLicense',
    label: 'I have a valid driver license.',
  },
  {
    key: 'licenseNotSuspendedOrRevoked',
    label: 'My driver license is not suspended or revoked.',
  },
  {
    key: 'hasActiveInsurance',
    label: 'I have active auto insurance.',
  },
  {
    key: 'understandsNoJGInsurance',
    label: 'I understand JG LocalOps does not provide auto insurance.',
  },
  {
    key: 'understandsNoBackgroundCheckClaim',
    label:
      'I understand Ride Ready does not mean JG LocalOps has run a background check.',
  },
  {
    key: 'understandsSelfAttestedRideInfo',
    label: 'I understand this ride-readiness information is self-attested.',
  },
  {
    key: 'willOnlyAcceptSafeLawfulRides',
    label: 'I will only accept safe, lawful ride help I am comfortable doing.',
  },
  {
    key: 'willConfirmRideDetailsBeforeStarting',
    label: 'I will confirm ride details before starting.',
  },
  {
    key: 'willNotAcceptEmergencyMedicalRides',
    label: 'I will not accept emergency medical rides.',
  },
  {
    key: 'willNotAcceptUnsupervisedMinorTransport',
    label: 'I will not accept unsupervised minor transport.',
  },
  {
    key: 'understandsMoreVerificationMayBeRequiredLater',
    label: 'I understand more verification may be required later.',
  },
];

const stepOrder = [
  { key: 'verifiedHelper', label: 'Earn Verified Helper' },
  { key: 'agePolicy', label: 'Meet ride-helper age policy' },
  { key: 'licenseInfo', label: 'Confirm driver license' },
  { key: 'insuranceInfo', label: 'Confirm auto insurance' },
  { key: 'vehicleInfo', label: 'Add vehicle basics' },
  { key: 'rideLimits', label: 'Set ride preferences' },
  { key: 'rideAcknowledgements', label: 'Accept ride acknowledgements' },
];

const emptyProfile = {
  hasValidDriverLicense: false,
  driverLicenseState: '',
  driverLicenseExpirationMonth: '',
  driverLicenseExpirationYear: '',
  hasActiveAutoInsurance: false,
  insuranceProviderName: '',
  insuranceExpirationMonth: '',
  insuranceExpirationYear: '',
  vehicle: {
    year: '',
    make: '',
    model: '',
    color: '',
    licensePlateState: '',
    licensePlateLast4: '',
    passengerSeatCount: '',
  },
  rideHelperCategories: [],
  rideServiceAreaNote: '',
  rideAvailabilityNote: '',
  maxDistanceMiles: '',
  acknowledgements: acknowledgementOptions.reduce((acc, item) => {
    acc[item.key] = false;
    return acc;
  }, {}),
};

const getAuthHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

const coerceInputValue = (value) =>
  value === null || value === undefined ? '' : String(value);

const mergeProfile = (profile = {}) => ({
  ...emptyProfile,
  ...profile,
  driverLicenseExpirationMonth: coerceInputValue(
    profile.driverLicenseExpirationMonth,
  ),
  driverLicenseExpirationYear: coerceInputValue(
    profile.driverLicenseExpirationYear,
  ),
  insuranceExpirationMonth: coerceInputValue(profile.insuranceExpirationMonth),
  insuranceExpirationYear: coerceInputValue(profile.insuranceExpirationYear),
  vehicle: {
    ...emptyProfile.vehicle,
    ...(profile.vehicle || {}),
    year: coerceInputValue(profile.vehicle?.year),
    passengerSeatCount: coerceInputValue(profile.vehicle?.passengerSeatCount),
  },
  rideHelperCategories: Array.isArray(profile.rideHelperCategories)
    ? profile.rideHelperCategories
    : [],
  maxDistanceMiles: coerceInputValue(profile.maxDistanceMiles),
  acknowledgements: {
    ...emptyProfile.acknowledgements,
    ...(profile.acknowledgements || {}),
  },
});

const getStepComplete = (readiness = {}, key) => {
  switch (key) {
    case 'verifiedHelper':
      return readiness.verifiedHelper === true;
    case 'agePolicy':
      return readiness.agePolicyComplete === true;
    case 'licenseInfo':
      return readiness.licenseInfoComplete === true;
    case 'insuranceInfo':
      return readiness.insuranceInfoComplete === true;
    case 'vehicleInfo':
      return readiness.vehicleInfoComplete === true;
    case 'rideLimits':
      return readiness.rideLimitsComplete === true;
    case 'rideAcknowledgements':
      return readiness.rideAcknowledgementsComplete === true;
    default:
      return false;
  }
};

const buildStatusCopy = (readiness = {}) => {
  if (readiness.rideReady) return 'Ride Ready earned.';
  if (readiness.verifiedHelper) {
    return 'Finish your private ride-readiness steps to become Ride Ready.';
  }
  return 'Become a Verified Helper first to unlock Ride Ready steps.';
};

export default function LocalOpsRideReadyCard({ cardRef, isFocused = false, token }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(emptyProfile);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [editingEarnedProfile, setEditingEarnedProfile] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axiosConn.get(
        'local-ops/ride-ready/me',
        getAuthHeaders(token),
      );
      const nextData = res.data?.data || {};
      setData(nextData);
      setForm(mergeProfile(nextData.profile));
      if (nextData.readiness?.rideReady) {
        setEditingEarnedProfile(false);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Could not load LocalOps Ride Ready status.',
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const readiness = data?.readiness || {};
  const rideReadyEarned = readiness.rideReady === true;
  const verifiedHelper = readiness.verifiedHelper === true;
  const showEarnedSummary = rideReadyEarned && !editingEarnedProfile;
  const statusCopy = data?.message || buildStatusCopy(readiness);
  const missingLabels = useMemo(
    () => new Set((readiness.missingSteps || []).map((step) => step.key)),
    [readiness.missingSteps],
  );

  const updateField = (field) => (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateVehicle = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      vehicle: {
        ...prev.vehicle,
        [field]: value,
      },
    }));
  };

  const toggleCategory = (category) => {
    setForm((prev) => {
      const current = new Set(prev.rideHelperCategories || []);
      if (current.has(category)) current.delete(category);
      else current.add(category);
      return { ...prev, rideHelperCategories: Array.from(current) };
    });
  };

  const toggleAcknowledgement = (key) => {
    setForm((prev) => ({
      ...prev,
      acknowledgements: {
        ...prev.acknowledgements,
        [key]: !prev.acknowledgements?.[key],
      },
    }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!token || !verifiedHelper) return;
    setSaving(true);
    setError('');
    setSavedMessage('');
    try {
      const res = await axiosConn.patch(
        'local-ops/ride-ready/me',
        form,
        getAuthHeaders(token),
      );
      const nextData = res.data?.data || {};
      setData(nextData);
      setForm(mergeProfile(nextData.profile));
      if (nextData.readiness?.rideReady) {
        setEditingEarnedProfile(false);
      }
      setSavedMessage('Ride Ready profile saved.');
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Could not save Ride Ready profile.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!token) return null;

  return (
    <section
      className={`localops-helper-profile-card localops-ride-ready-card${
        isFocused ? ' is-focused' : ''
      }`}
      data-testid="localops-ride-ready-card"
      ref={cardRef}
    >
      <div className="localops-helper-profile-card__header">
        <div>
          <div className="profile-completion-eyebrow">JG LocalOps</div>
          <h3>{rideReadyEarned ? 'Ride Ready' : 'Complete Ride Ready steps'}</h3>
          <p>{statusCopy}</p>
        </div>
        <span
          className={`localops-helper-profile-card__badge${
            rideReadyEarned ? ' is-earned' : ''
          }`}
        >
          {rideReadyEarned ? 'Ride Ready' : 'Ride readiness'}
        </span>
      </div>

      {showEarnedSummary ? (
        <div
          className="localops-helper-profile-earned"
          data-testid="localops-ride-ready-earned-summary"
        >
          <div className="localops-helper-profile-earned__status">
            <strong>Ride Ready complete</strong>
            <span>Verified Helper + ride-readiness steps</span>
          </div>
          <div
            aria-label="Ride Ready private readiness complete"
            className="localops-helper-profile-earned__chips"
          >
            <span>Verified Helper</span>
            <span>License self-attested</span>
            <span>Insurance self-attested</span>
            <span>Vehicle info added</span>
            <span>Ride limits set</span>
          </div>
          <p>
            Keep this current before accepting any future ride-style LocalOps
            work.
          </p>
          <div className="localops-helper-profile-actions">
            <button
              className="profile-completion-button"
              onClick={() => setEditingEarnedProfile(true)}
              type="button"
            >
              Edit Ride Ready profile
            </button>
            <button
              className="localops-helper-profile-secondary"
              disabled={loading}
              onClick={fetchProfile}
              type="button"
            >
              Refresh Ride Ready status
            </button>
          </div>
          <p className="localops-helper-profile-privacy">
            {data?.privacy ||
              'Your ride-readiness details are private and are not shown publicly.'}
          </p>
          <p className="localops-helper-profile-privacy">
            {data?.honesty ||
              'Ride Ready does not mean JG LocalOps has run a background check or provides auto insurance.'}
          </p>
          {loading ? (
            <p className="localops-helper-profile-status">Loading...</p>
          ) : null}
          {savedMessage ? (
            <p className="localops-helper-profile-status is-success">
              {savedMessage}
            </p>
          ) : null}
          {error ? (
            <p className="localops-helper-profile-status is-error">{error}</p>
          ) : null}
        </div>
      ) : (
        <>
          <ul className="localops-helper-profile-steps">
            {stepOrder.map((step) => {
              const done = getStepComplete(readiness, step.key);
              return (
                <li key={step.key} className={done ? 'is-complete' : ''}>
                  <span>{done ? 'Done' : 'Next'}</span>
                  <strong>{step.label}</strong>
                  {missingLabels.has(step.key) ? <small>Needed</small> : null}
                </li>
              );
            })}
          </ul>

          {!verifiedHelper ? (
            <div className="localops-ride-ready-locked">
              <strong>Verified Helper comes first</strong>
              <p>
                Complete your helper profile and first verified LocalOps task
                before Ride Ready can be completed.
              </p>
              <p className="localops-helper-profile-privacy">
                {data?.privacy ||
                  'Your ride-readiness details are private and are not shown publicly.'}
              </p>
              <p className="localops-helper-profile-privacy">
                {data?.honesty ||
                  'Ride Ready does not mean JG LocalOps has run a background check or provides auto insurance.'}
              </p>
              {loading ? (
                <p className="localops-helper-profile-status">Loading...</p>
              ) : null}
              {error ? (
                <p className="localops-helper-profile-status is-error">
                  {error}
                </p>
              ) : null}
            </div>
          ) : (
            <form className="localops-helper-profile-form" onSubmit={saveProfile}>
              <section className="localops-ride-ready-group">
                <h4>Driver license</h4>
                <label className="localops-helper-profile-checkbox">
                  <input
                    checked={form.hasValidDriverLicense}
                    onChange={updateField('hasValidDriverLicense')}
                    type="checkbox"
                  />
                  <span>I have a valid driver license</span>
                </label>
                <div className="localops-helper-profile-grid">
                  <label>
                    <span>License state</span>
                    <input
                      autoComplete="address-level1"
                      maxLength={2}
                      onChange={updateField('driverLicenseState')}
                      placeholder="TN"
                      type="text"
                      value={form.driverLicenseState}
                    />
                  </label>
                  <label>
                    <span>License expiration month</span>
                    <input
                      inputMode="numeric"
                      max="12"
                      min="1"
                      onChange={updateField('driverLicenseExpirationMonth')}
                      placeholder="12"
                      type="number"
                      value={form.driverLicenseExpirationMonth}
                    />
                  </label>
                  <label>
                    <span>License expiration year</span>
                    <input
                      inputMode="numeric"
                      maxLength={4}
                      onChange={updateField('driverLicenseExpirationYear')}
                      placeholder="2028"
                      type="number"
                      value={form.driverLicenseExpirationYear}
                    />
                  </label>
                </div>
              </section>

              <section className="localops-ride-ready-group">
                <h4>Auto insurance</h4>
                <label className="localops-helper-profile-checkbox">
                  <input
                    checked={form.hasActiveAutoInsurance}
                    onChange={updateField('hasActiveAutoInsurance')}
                    type="checkbox"
                  />
                  <span>I have active auto insurance</span>
                </label>
                <div className="localops-helper-profile-grid">
                  <label>
                    <span>Insurance provider name</span>
                    <input
                      autoComplete="organization"
                      onChange={updateField('insuranceProviderName')}
                      placeholder="Provider name"
                      type="text"
                      value={form.insuranceProviderName}
                    />
                  </label>
                  <label>
                    <span>Insurance expiration month</span>
                    <input
                      inputMode="numeric"
                      max="12"
                      min="1"
                      onChange={updateField('insuranceExpirationMonth')}
                      placeholder="12"
                      type="number"
                      value={form.insuranceExpirationMonth}
                    />
                  </label>
                  <label>
                    <span>Insurance expiration year</span>
                    <input
                      inputMode="numeric"
                      maxLength={4}
                      onChange={updateField('insuranceExpirationYear')}
                      placeholder="2028"
                      type="number"
                      value={form.insuranceExpirationYear}
                    />
                  </label>
                </div>
              </section>

              <section className="localops-ride-ready-group">
                <h4>Vehicle basics</h4>
                <div className="localops-helper-profile-grid">
                  <label>
                    <span>Vehicle year</span>
                    <input
                      inputMode="numeric"
                      onChange={updateVehicle('year')}
                      placeholder="2021"
                      type="number"
                      value={form.vehicle.year}
                    />
                  </label>
                  <label>
                    <span>Make</span>
                    <input
                      autoComplete="off"
                      onChange={updateVehicle('make')}
                      placeholder="Toyota"
                      type="text"
                      value={form.vehicle.make}
                    />
                  </label>
                  <label>
                    <span>Model</span>
                    <input
                      autoComplete="off"
                      onChange={updateVehicle('model')}
                      placeholder="Camry"
                      type="text"
                      value={form.vehicle.model}
                    />
                  </label>
                  <label>
                    <span>Color</span>
                    <input
                      autoComplete="off"
                      onChange={updateVehicle('color')}
                      placeholder="Silver"
                      type="text"
                      value={form.vehicle.color}
                    />
                  </label>
                  <label>
                    <span>Plate state</span>
                    <input
                      autoComplete="address-level1"
                      maxLength={2}
                      onChange={updateVehicle('licensePlateState')}
                      placeholder="TN"
                      type="text"
                      value={form.vehicle.licensePlateState}
                    />
                  </label>
                  <label>
                    <span>License plate last 4</span>
                    <input
                      autoComplete="off"
                      maxLength={8}
                      onChange={updateVehicle('licensePlateLast4')}
                      placeholder="1234"
                      type="text"
                      value={form.vehicle.licensePlateLast4}
                    />
                  </label>
                  <label>
                    <span>Passenger seats available</span>
                    <input
                      inputMode="numeric"
                      min="1"
                      onChange={updateVehicle('passengerSeatCount')}
                      placeholder="3"
                      type="number"
                      value={form.vehicle.passengerSeatCount}
                    />
                  </label>
                </div>
              </section>

              <fieldset className="localops-helper-profile-categories">
                <legend>Ride helper categories</legend>
                {rideCategoryOptions.map((category) => (
                  <label key={category}>
                    <input
                      checked={(form.rideHelperCategories || []).includes(
                        category,
                      )}
                      onChange={() => toggleCategory(category)}
                      type="checkbox"
                    />
                    <span>{category}</span>
                  </label>
                ))}
              </fieldset>

              <label className="localops-helper-profile-note">
                <span>Ride service area</span>
                <textarea
                  onChange={updateField('rideServiceAreaNote')}
                  placeholder="Camden and nearby Benton County."
                  rows={3}
                  value={form.rideServiceAreaNote}
                />
              </label>
              <label className="localops-helper-profile-note">
                <span>Ride availability</span>
                <textarea
                  onChange={updateField('rideAvailabilityNote')}
                  placeholder="Weekends and some weekday afternoons."
                  rows={3}
                  value={form.rideAvailabilityNote}
                />
              </label>
              <label>
                <span>Maximum ride distance in miles</span>
                <input
                  inputMode="numeric"
                  max="500"
                  min="1"
                  onChange={updateField('maxDistanceMiles')}
                  placeholder="25"
                  type="number"
                  value={form.maxDistanceMiles}
                />
              </label>

              <fieldset className="localops-ride-ready-acks">
                <legend>Ride acknowledgements</legend>
                {acknowledgementOptions.map((item) => (
                  <label key={item.key}>
                    <input
                      checked={form.acknowledgements?.[item.key] === true}
                      onChange={() => toggleAcknowledgement(item.key)}
                      type="checkbox"
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </fieldset>

              <div className="localops-helper-profile-actions">
                <button
                  className="profile-completion-button"
                  disabled={saving || loading}
                  type="submit"
                >
                  {saving ? 'Saving...' : 'Save Ride Ready profile'}
                </button>
                <button
                  className="localops-helper-profile-secondary"
                  disabled={loading}
                  onClick={fetchProfile}
                  type="button"
                >
                  Refresh Ride Ready status
                </button>
                {rideReadyEarned ? (
                  <button
                    className="localops-helper-profile-secondary"
                    onClick={() => setEditingEarnedProfile(false)}
                    type="button"
                  >
                    Done editing Ride Ready
                  </button>
                ) : null}
              </div>

              <p className="localops-helper-profile-privacy">
                {data?.privacy ||
                  'Your ride-readiness details are private and are not shown publicly.'}
              </p>
              <p className="localops-helper-profile-privacy">
                {data?.honesty ||
                  'Ride Ready does not mean JG LocalOps has run a background check or provides auto insurance.'}
              </p>
              {loading ? (
                <p className="localops-helper-profile-status">Loading...</p>
              ) : null}
              {savedMessage ? (
                <p className="localops-helper-profile-status is-success">
                  {savedMessage}
                </p>
              ) : null}
              {error ? (
                <p className="localops-helper-profile-status is-error">
                  {error}
                </p>
              ) : null}
            </form>
          )}
        </>
      )}
    </section>
  );
}
